define((require) => {
    "use strict";

    const {debounce}                  = require("underscore");
    const $                           = require("jquery");
    const EditorEvents                = require("shared/services/EditorEvents");
    const EditorWebFontController     = require("editor/ui/experience/editor/EditorWebFontController");
    const FontProvider                = require("shared/data/fonts/FontProvider");
    const FontsDotComApi              = require("editor/services/fonts/FontsDotComApi");
    const FontsDotComIntegrationModel = require("editor/ui/settings/integrations/FontsDotComIntegrationModel");
    const {Logger}                      = require("shared/util/Logger");
    const ManageFontsIntegrationView  = require("editor/ui/settings/fonts/ManageFontsIntegrationView");
    const Marionette                  = require("marionette");
    const {isBlank}                   = require("shared/util/StringUtils");
    const SystemMessages              = require("editor/services/SystemMessages");
    const TypekitApi                  = require("editor/services/fonts/TypekitApi");
    const TypekitIntegrationModel     = require("editor/ui/settings/integrations/TypekitIntegrationModel");

    const logger = new Logger("ManageFontsController");

    const FONTS_DOT_COM = "Fonts.com";
    const TYPEKIT = "Typekit";

    return Marionette.Controller.extend({

        showManageFonts(integrations) {
            const view = new ManageFontsIntegrationView({integrations});
            const debouncedSaveIntegration = debounce(this._integrationChangedHandler, 1000).bind(this);

            this.options.region.show(view);

            // Listen to validated changes...
            this.listenTo(view, "validated", (model) => {
                debouncedSaveIntegration(model);
            });
        },

        /**
         * @private
         *
         * Callback when a potential valid Typekit Api token has been entered, which
         * performs actual token validation and font cleanup.
         */
        _integrationChangedHandler(model) {
            if (model instanceof FontsDotComIntegrationModel) {
                this._saveFontsDotComIntegration(model);
            } else if (model instanceof TypekitIntegrationModel) {
                this._saveTypekitIntegration(model);
            } else {
                logger.error("Invalid fonts integration passed.", model);
            }
        },

        _saveFontsDotComIntegration(fontsDotComIntegration) {
            const authenticationKey = fontsDotComIntegration.get("authenticationKey");

            if (isBlank(authenticationKey)) {
                fontsDotComIntegration.save().then(() => {
                    EditorWebFontController.clearFontType(FontProvider.FONTS_DOT_COM);
                    SystemMessages.ManageFonts.removedKey(FONTS_DOT_COM, "authentication key");

                    const eventProperties = {
                        category: "Fonts",
                        integrationType: FONTS_DOT_COM,
                        triggerOrigin: "ApiTokenInputField"
                    };

                    EditorEvents.trigger(EditorEvents.constants.DELETE_INTEGRATION, eventProperties);
                });
                return;
            }

            this._validateFontsDotComAuthenticationKey(authenticationKey)
                .then((valid) => {
                    if (valid) {
                        logger.info("Fonts.com authentication key is valid.");
                        fontsDotComIntegration.save().then(() => {
                            SystemMessages.ManageFonts.validKey(FONTS_DOT_COM, "authentication key", "fontsDotCom:valid:authenticationKey");
                            EditorWebFontController.reloadFonsDotComFonts();
                        });

                        const eventProperties = {
                            category: "Fonts",
                            integrationType: FONTS_DOT_COM,
                            triggerOrigin: "ApiTokenInputField"
                        };

                        EditorEvents.trigger(EditorEvents.constants.SAVE_INTEGRATION, eventProperties);
                    } else {
                        SystemMessages.ManageFonts.invalidKey(FONTS_DOT_COM, "authentication key", authenticationKey, "fontsDotCom:valid:authenticationKey");
                    }
                });
        },

        _saveTypekitIntegration(typekitIntegration) {
            const typekitToken = typekitIntegration.get("typekitApiToken");

            if (isBlank(typekitToken)) {
                typekitIntegration.save().then(() => {
                    EditorWebFontController.clearFontType(FontProvider.TYPEKIT);
                    SystemMessages.ManageFonts.removedKey(TYPEKIT, "API token");

                    const eventProperties = {
                        category: "Fonts",
                        integrationType: TYPEKIT,
                        triggerOrigin: "ApiTokenInputField"
                    };

                    EditorEvents.trigger(EditorEvents.constants.DELETE_INTEGRATION, eventProperties);
                });
                return;
            }

            // Validate token before loading anything
            this._validateTypekitToken(typekitToken)
                .then((valid) => {
                    if (valid) {
                        logger.info("Token is valid - fetching Typekit data");
                        typekitIntegration.save().then(() => {
                            SystemMessages.ManageFonts.validKey(TYPEKIT, "API token", "typekit:valid:token");
                            EditorWebFontController.reloadKits(typekitToken);

                            const eventProperties = {
                                category: "Fonts",
                                integrationType: TYPEKIT,
                                triggerOrigin: "ApiTokenInputField"
                            };

                            EditorEvents.trigger(EditorEvents.constants.SAVE_INTEGRATION, eventProperties);
                        });
                    } else {
                        SystemMessages.ManageFonts.invalidKey(TYPEKIT, "API token", typekitToken, "typekit:valid:token");
                    }
                });
        },

        _validateFontsDotComAuthenticationKey(authenticationKey) {
            return new $.Deferred((deferred) => {
                const failed = () => {
                    deferred.resolve(false);
                };

                FontsDotComApi.validate(authenticationKey)
                    .then((value) => {
                        if (value && value.errors) {
                            failed();
                        } else {
                            deferred.resolve(true);
                        }
                    })
                    .fail(failed);
            }).promise();
        },

        /**
         * @private
         *
         * Validates the specified Typekit API token, by performing an API request, that if proves
         * successful, resolves the returned promise with a boolean value of <tt>true</tt>, otherwise <tt>false</tt>.
         *
         * @param  {String} token The API token.
         * @return {Promise} A promise the resolves to either <tt>true</tt> or <tt>false</tt>.
         */
        _validateTypekitToken(token) {
            return new $.Deferred((deferred) => {
                const typekitApi = new TypekitApi(token),
                    failed = () => {
                        deferred.resolve(false);
                    };

                typekitApi.kits()
                    .then((value) => {
                        if (value && value.errors) {
                            failed();
                        } else {
                            deferred.resolve(true);
                        }
                    })
                    .fail(failed);
            }).promise();
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/settings/fonts/ManageFontsController.js